import { defineComponent as _defineComponent } from 'vue'
import { useDebounceFn } from "@vueuse/core";
/**
 * This component handles everything related to autocomplete search. Its parent components do not need autocomplete
 * logic in them since the two functions of this component are 1) allow users to search by names for specific profiles,
 * showing results in a dropdown, and 2) allow users to enter keywords that will be used in addition to the advanced
 * search form (its parent component) to filter results.
 * Therefore, this component only needs to notify its parent components when "keyword" is updated, and then the buttons are
 * clicked as far as component communication goes.
 */
import { computed, onMounted, Ref, ref, watch } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router/composables";

import { ApiService, LogService, SnackbarService } from "@/services";
import {
  SearchItem,
  VignetteEndpoints,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";

import HideButton from "@/components/buttons/HideSearchFormButton.vue";
import SearchButtons from "@/components/buttons/SearchButtons.vue";
import { useSearchStore } from "@/pinia/search";
import { useVuetify } from "@/composition/useVuetify";


export default /*#__PURE__*/_defineComponent({
  __name: 'KeywordSearch',
  props: {
  label: {
    type: String,
    default: "Keyword",
    required: false,
  },
  bgColor: {
    type: String,
    default: "white",
  },
  endpoint: {
    type: String as () => VignetteEndpoints,
    default: VignetteEndpoints.API_SEARCH_VIGNETTE,
  },
  hidePayWalledProfiles: {
    type: Boolean,
    default: false,
  },
  noAutoFocus: {
    type: Boolean,
    default: false,
  },
  hideSearchButtons: {
    type: Boolean,
    default: false,
  },
  hideHideButton: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "",
  },
  hint: {
    type: String,
    default: "",
  },
  persistentHint: {
    type: Boolean,
    default: false,
  },
  maxHints: {
    type: Number,
    default: undefined,
    required: false,
  },
},
  emits: ["update:keyword", "search", "clear"],
  setup(__props, { emit }: { emit: ({
  (e: "update:keyword", val: string): void;
  (e: "search"): void;
  (e: "clear"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;




const router = useRouter();

const vuetify = useVuetify();

const searchStore = useSearchStore();
const query = computed(() => searchStore.search.currentSearchQuery);

const selectedProfile = ref<string | SearchItem | null>(null);
watch(selectedProfile, (v: SearchItem | null | string) => {
  if (v !== null && typeof v !== "string") {
    const route = VignetteHelpers.getRouteFromSearchItemType(
      v.recordType ?? ""
    );
    const id = String(v.id);
    const name = VignetteHelpers.getProfileNameSlug(v.preferredNameDisplay);
    search.value = "";
    router.push({
      name: route,
      params: {
        id,
        name,
      },
    });
  }
});

const search = computed({
  get: () => query.value.keyword,
  set: (v: string | null) =>
    searchStore.setCurrentSearchQueryField({
      field: "keyword",
      value: v ?? "",
    }),
});
const activated = ref(false);
watch(search, (v: string | null) => {
  if (v === "" || v === null) {
    selectedProfile.value = "";
  }
  handleUpdatedSearch();
});

const debounceTime = computed(() => (search.value.length == 3 ? 600 : 300));

const handleUpdatedSearch = useDebounceFn(async () => {
  if (
    search.value.length >= 3 &&
    isLoading.value === false &&
    activated.value
  ) {
    isLoading.value = true;
    try {
      items.value = await ApiService.getAutocompleteSearchItems(
        search.value,
        props.endpoint,
        props.hidePayWalledProfiles
      );
    } catch (e) {
      LogService.error("KeywordSearch failed", e as Error);
    } finally {
      isLoading.value = false;
    }
  } else {
    items.value = [];
  }
}, debounceTime.value);

const handleSearch = () => {
  if (router.currentRoute.meta?.searchRoute) {
    if (!search.value) {
      SnackbarService.displaySnackbar("Please enter a search keyword.");
      return;
    }
    router.push({
      name: router.currentRoute.meta?.searchRoute,
      query: { q: search.value },
    });
  } else {
    emit("search");
    keywordSearch.value?.blur();
  }
};

const computedLabel = computed(() => {
  const routeLabel = router.currentRoute.meta?.searchLabel;
  const propLabel = props.label;
  return routeLabel || propLabel || "Keyword Search";
});
const computedPlaceholder = computed(() => {
  const propPlaceholder = props.placeholder;
  return (
    propPlaceholder ||
    "Enter a search keyword or select from advanced search filters"
  );
});
const hint = computed(() => {
  const routeHint = router.currentRoute.meta?.searchHint;
  const propHint = props.hint;
  let hintText =
    routeHint ||
    propHint ||
    router.resolve({ name: VignetteRoutes.SearchUniversal }).route.meta
      ?.searchHint ||
    "";

  // If maxHints is provided and the hint contains examples, limit the number of examples
  if (props.maxHints !== undefined && hintText.includes("Examples:")) {
    const examplesPrefix = hintText.split("Examples:")[0] + "Examples:";
    const examplesText = hintText.split("Examples:")[1].trim();
    const examples = examplesText.split(", ").slice(0, props.maxHints);
    hintText = `${examplesPrefix} ${examples.join(", ")}`;
  }

  return hintText;
});

const getComboboxMaskedLabel = (text: string, isFormer: boolean): string => {
  let start: string,
    middle = "",
    end = "";

  const archived = isFormer
    ? '<i aria-hidden="true" style="font-size: 20px;" class="pl-1 v-icon material-icons theme--light">history_toggle_off</i>'
    : "";
  const index = text.toLocaleLowerCase().indexOf(search.value);

  if (index < 0) {
    start = text;
  } else {
    start = text.slice(0, index).replace(/ /g, "\u00a0");
    middle = text
      .slice(index, index + search.value.length)
      .replace(/ /g, "\u00a0");
    end = text.slice(index + search.value.length).replace(/ /g, "\u00a0");
  }

  return `${start}<span class="v-list-item__mask">${middle}</span>${end} ${archived}`;
};

const isLoading = ref(false);
const items: Ref<SearchItem[]> = ref([]);

const keywordSearch = ref<HTMLInputElement | null>(null);

const autoFocus = computed(() => {
  return (
    !props.noAutoFocus &&
    Object.keys(router.currentRoute.query).length === 0 &&
    vuetify.breakpoint.mdAndUp
  );
});

onMounted(() => {
  if (search.value && !router.currentRoute.meta?.searchRoute) {
    selectedProfile.value = search.value;
  }
  activated.value = true;
});
onBeforeRouteLeave((_, from, next) => {
  if (from.name?.toLowerCase().includes("search")) {
    searchStore.resetSearch();
  }
  next();
});

return { __sfc: true,props, emit, router, vuetify, searchStore, query, selectedProfile, search, activated, debounceTime, handleUpdatedSearch, handleSearch, computedLabel, computedPlaceholder, hint, getComboboxMaskedLabel, isLoading, items, keywordSearch, autoFocus, HideButton, SearchButtons }
}

})